import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { OrientationToggle, PendingPhoto } from "@/components/site/Lamina";
import { FLOAT_TEXT, MAGNET_TEXT, PACKAGING_TEXT, ROUNDED_TEXT } from "@/content/texts";
import { dims, formatCOP, nativeOrientation, useCatalog, useSeo } from "@/lib/site";
import { NotFoundPage } from "@/pages/site/OtherPages";

const Feature = ({ title, text }) => (
  <div className="border-t border-[#EAE7E2] py-6">
    <h3 className="text-sm font-semibold uppercase tracking-[0.12em]">{title}</h3>
    <p className="mt-3 text-base leading-relaxed text-[#080808]/75">{text}</p>
  </div>
);

const ProductGallery = ({ photos, size }) => {
  const [sel, setSel] = useState(0);
  const cur = photos[sel] || photos[0];
  const rest = photos.filter((ph) => ph !== cur);
  return (
    <div data-testid="product-gallery" className="space-y-4">
      {cur ? <img key={cur.url} data-testid="product-gallery-main" src={cur.url} alt={`Lámina LUMINA ${size}`} className="mx-auto block h-auto max-h-[80vh] w-full object-contain" />
        : <PendingPhoto testId="product-photo-pending-main" className="aspect-square text-[0.7rem]" />}
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => rest[i] ? (
          <button key={rest[i].id} type="button" data-testid={`product-gallery-thumb-${i}`} onClick={() => setSel(photos.indexOf(rest[i]))} aria-label={`Ver foto ${i + 2}`}
            className="overflow-hidden opacity-90 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B18A4B]">
            <img src={rest[i].url} alt="" className="aspect-square w-full bg-white object-contain" />
          </button>
        ) : <PendingPhoto key={`p${i}`} testId={`product-photo-pending-${i + 1}`} className="aspect-square text-[0.6rem]" />)}
      </div>
    </div>
  );
};

export default function ProductPage() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const { data, isLoading } = useCatalog();
  const p = data?.products.find((x) => x.slug === slug);
  const orientation = params.get("orientacion") || (p ? nativeOrientation(p) : "horizontal");
  useSeo({
    title: p ? `Lámina magnética flotante ${p.size} personalizada | LUMINA STUDIO` : "Producto | LUMINA STUDIO",
    description: "Lámina magnética flotante personalizada con tu fotografía o un collage, con bordes redondeados y efecto flotante.",
  });
  if (isLoading) return <div className="min-h-[60vh]" />;
  if (!p || !p.available) return <NotFoundPage />;
  const d = dims(p, orientation);
  const photos = p.photos?.[orientation] || [];

  return (
    <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:px-12 py-12 sm:py-20 lg:grid-cols-[1.1fr_1fr]">
      <div className="space-y-4">
        <ProductGallery key={orientation} photos={photos} size={p.size} />
      </div>
      <div>
        <p className="text-[0.75rem] uppercase tracking-[0.16em] text-[#B18A4B]">Tamaño {p.size}</p>
        <h1 data-testid="product-name" className="mt-3 font-serif-display text-[34px] sm:text-[48px] font-medium leading-tight">{p.name}</h1>
        <p data-testid="product-dims" className="mt-4 text-lg">{d.w} × {d.h} cm · {orientation === "horizontal" ? "Horizontal" : "Vertical"}</p>
        <p data-testid="product-price" className="mt-2 text-2xl font-medium">{formatCOP(p.price_cop)}</p>
        <div className="mt-8">
          <OrientationToggle value={orientation} onChange={(o) => setParams({ orientacion: o }, { replace: true })} testId="product-orientation" />
        </div>
        <p className="mt-8 text-base leading-relaxed text-[#080808]/80">{p.description}</p>
        <Link to={`/personalizar?talla=${p.slug}&orientacion=${orientation}`} data-testid="product-customize-button"
          className="mt-8 flex justify-center rounded-full bg-[#080808] px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#FAF8F4] transition-colors hover:bg-[#B18A4B] sm:inline-flex">
          Personalizar mi LUMINA
        </Link>
        <div className="mt-12">
          <Feature title="Efecto flotante" text={FLOAT_TEXT} />
          <Feature title="Bordes redondeados" text={ROUNDED_TEXT} />
          <Feature title="Sistema magnético" text={MAGNET_TEXT} />
          <Feature title="Empaque protector" text={PACKAGING_TEXT} />
          <div className="border-t border-[#EAE7E2] py-6">
            <Link to="/como-funciona#guia" data-testid="product-guide-link" className="text-sm font-semibold uppercase tracking-[0.12em] underline underline-offset-4 hover:text-[#B18A4B]">Ver guía de instalación</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
